/**
 * PHASE 9 — dynamic solar-system objects in the searchable sky.
 *
 * The Sun, Moon and planets have no fixed catalog coordinate: their J2000
 * position changes every instant. This module provides:
 *
 *   - describeDynamicObject(id): metadata for search results / info cards
 *     (no position; the object model marks these with source "ephemeris").
 *   - bodyPositionJ2000(body, observer, time): the body's true-of-date
 *     topocentric coordinate converted to J2000, so the rest of the app
 *     (SeeIt guidance, picking, renderer) can treat every searchable object
 *     uniformly as a J2000 RA/Dec.
 *
 * The J2000 conversion inverts the same precession rotation the engine uses
 * for catalog stars (ofDateToJ2000Rotation), so SeeIt guidance matches the
 * drawn sky. This module stays inside the astronomy/catalog layer: it has no
 * UI or rendering imports.
 */

import { MakeTime, RotateVector, SphereFromVector, Vector } from "astronomy-engine";

import { bodyEphemeris, moonAgeDays, PLANETS } from "../astro/ephemeris";
import { ofDateToJ2000Rotation, skyEngine } from "../astro/engine";
import type { ObserverLocation, ObserverTime, SolarSystemBody } from "../astro/types";
import { Planet } from "../astro/types";
import type { CelestialObject } from "./objectModel";

/** All dynamically positioned bodies, in display order: Sun, Moon, planets. */
export const DYNAMIC_BODIES: readonly SolarSystemBody[] = [
  "Sun",
  "Moon",
  ...PLANETS,
];

/** Searchable id for a body, e.g. "BODY:Jupiter". */
export function bodyId(body: SolarSystemBody): string {
  return `BODY:${body}`;
}

/** Resolve a BODY: id back to the body name, or null. */
export function bodyFromId(id: string): SolarSystemBody | null {
  if (!id.startsWith("BODY:")) return null;
  const name = id.slice(5);
  return DYNAMIC_BODIES.some((b) => b === name) ? (name as SolarSystemBody) : null;
}

const PLANET_ALIASES: Readonly<Record<Planet, readonly string[]>> = {
  [Planet.Mercury]: ["Mercury", "Mercurio"],
  [Planet.Venus]: ["Venus", "Evening Star", "Morning Star"],
  [Planet.Mars]: ["Mars", "Red Planet"],
  [Planet.Jupiter]: ["Jupiter", "Jove"],
  [Planet.Saturn]: ["Saturn"],
  [Planet.Uranus]: ["Uranus"],
  [Planet.Neptune]: ["Neptune"],
};

/** Search aliases for a body (proper name first). */
export function bodySearchTerms(body: SolarSystemBody): readonly string[] {
  if (body === "Sun") return ["Sun", "Sol"];
  if (body === "Moon") return ["Moon", "Luna"];
  return PLANET_ALIASES[body as Planet];
}

/**
 * A searchable catalog entry for a dynamic body. `ra`/`dec` are meaningless
 * (the body moves); consumers must resolve positions with
 * bodyPositionJ2000. magnitude is a placeholder for sorting/sizing and is
 * replaced by the ephemeris value when displayed.
 */
export function describeDynamicObject(id: string): CelestialObject | null {
  const body = bodyFromId(id);
  if (!body) return null;
  const type = body === "Sun" ? "SUN" : body === "Moon" ? "MOON" : "PLANET";
  return {
    id,
    name: body,
    type,
    ra: 0,
    dec: 0,
    magnitude: 0,
    source: "ephemeris",
    aliases: [...bodySearchTerms(body)],
    description: bodyDescription(body),
    provenance: "astronomy-engine ephemeris (VSOP87 / ELP-class)",
  };
}

function bodyDescription(body: SolarSystemBody): string | undefined {
  switch (body) {
    case "Sun":
      return "The star at the centre of our solar system.";
    case "Moon":
      return "Earth's natural satellite.";
    case Planet.Mercury:
      return "The innermost planet, small and fast, always near the Sun in the sky.";
    case Planet.Venus:
      return "The brightest planet, sometimes called the Evening or Morning Star.";
    case Planet.Mars:
      return "The Red Planet, famous for its rust-coloured surface.";
    case Planet.Jupiter:
      return "The largest planet, a gas giant with a system of moons.";
    case Planet.Saturn:
      return "The ringed giant, the second-largest planet.";
    case Planet.Uranus:
      return "An ice giant tipped on its side, visible only in dark skies.";
    case Planet.Neptune:
      return "The most distant planet, requiring binoculars or a telescope.";
  }
}

/**
 * Topocentric position of a body converted to the J2000 frame (RA hours,
 * Dec degrees), matching the frame every static catalog object uses.
 */
export function bodyPositionJ2000(
  body: SolarSystemBody,
  observer: ObserverLocation,
  time: ObserverTime,
): { raHours: number; decDeg: number } {
  const ofDate = skyEngine.bodyCoordinate(body, observer, time);
  const rot = ofDateToJ2000Rotation(time);
  const v = RotateVector(rot, VectorFromRaDec(ofDate.rightAscensionHours, ofDate.declinationDegrees, time));
  const s = SphereFromVector(v);
  return { raHours: s.lon / 15, decDeg: s.lat };
}

function VectorFromRaDec(raHours: number, decDeg: number, time: ObserverTime): Vector {
  const dec = decDeg * (Math.PI / 180);
  const ra = raHours * 15 * (Math.PI / 180);
  const cosDec = Math.cos(dec);
  return new Vector(cosDec * Math.cos(ra), cosDec * Math.sin(ra), Math.sin(dec), MakeTime(new Date(time.utcMilliseconds)));
}

/** Full ephemeris summary for the info card (magnitude, distance, phase). */
export function bodyDisplayInfo(body: SolarSystemBody, observer: ObserverLocation, time: ObserverTime): {
  magnitude: number;
  distanceAu: number;
  angularDiameterArcseconds: number;
  illuminatedFraction: number;
  moonAgeDays: number | null;
} {
  const eph = bodyEphemeris(body, observer, time);
  return {
    magnitude: eph.magnitude,
    distanceAu: eph.distanceAu,
    angularDiameterArcseconds: eph.angularDiameterArcseconds,
    illuminatedFraction: eph.illumination.fraction,
    moonAgeDays: body === "Moon" ? moonAgeDays(time) : null,
  };
}